/**
 * Imperative GLB templates for track hazards (rivals).
 * Crates/barriers stay procedural — neon-readable at speed and tiny in the IPA.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  makeBarrierMesh,
  makeCrateMesh,
  makeHoleMesh,
  makeLaneGlow,
  makeRivalMesh,
  makeTruckMesh,
} from "./models";

const RIVAL_PATHS = [
  "/models/low_poly_car_1.glb",
  "/models/low_poly_car_4.glb",
  "/models/low_poly_car_6.glb",
  "/models/super_car.glb",
] as const;

/** Match player VehicleModel footprint so traffic reads as the same fleet. */
const RIVAL_TARGET_LEN = 1.95;
const RIVAL_MAX_HEIGHT = 1.18;

let ready = false;
let loading: Promise<void> | null = null;
const rivalTemplates: THREE.Object3D[] = [];

function stripJunkMeshes(root: THREE.Object3D) {
  const doomed: THREE.Object3D[] = [];
  root.traverse((child) => {
    const name = (child.name || "").toLowerCase();
    // Don't strip `_tr` — some Sketchfab bodies are named that way and
    // removing them leaves only an antenna / exhaust (the "red stick").
    if (
      name.includes("collider") ||
      name.includes("collision") ||
      name.includes("shadow")
    ) {
      doomed.push(child);
    }
  });
  for (const o of doomed) o.parent?.remove(o);
}

/** Drop antennas / leftover poles that are <2% of the largest mesh volume. */
function dropTinyMeshes(root: THREE.Object3D) {
  const entries: { obj: THREE.Object3D; vol: number }[] = [];
  const tmp = new THREE.Box3();
  const size = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
    tmp.getSize(size);
    entries.push({
      obj: child,
      vol: Math.max(1e-8, size.x * size.y * size.z),
    });
  });
  if (!entries.length) return;
  const maxVol = Math.max(...entries.map((e) => e.vol));
  for (const e of entries) {
    if (e.vol < maxVol * 0.02) e.obj.parent?.remove(e.obj);
  }
}

/**
 * Sketchfab packs ship alphaMode:BLEND / double-sided glass & decals.
 * At speed that reads as “see-through crushed roof” — force solid bodies
 * and depth-writing glass.
 */
function hardenMaterials(root: THREE.Object3D) {
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const cloned = mats.map((m) => (m as THREE.Material).clone());
    mesh.material = cloned.length === 1 ? cloned[0]! : cloned;

    for (const m of cloned) {
      const mat = m as THREE.MeshStandardMaterial;
      if (!mat) continue;
      const name = (mat.name || "").toLowerCase();
      const looksGlass =
        name.includes("glass") ||
        name.includes("window") ||
        ((mat.transparent || mat.opacity < 0.98) && mat.opacity < 0.85);

      if (looksGlass) {
        mat.transparent = true;
        mat.opacity = Math.min(0.55, Math.max(0.3, mat.opacity || 0.45));
        mat.depthWrite = true;
      } else {
        mat.transparent = false;
        mat.opacity = 1;
        mat.depthWrite = true;
        if ("alphaMap" in mat) mat.alphaMap = null;
        if ("alphaTest" in mat) mat.alphaTest = 0;
      }
      mat.side = looksGlass ? THREE.FrontSide : THREE.DoubleSide;
      mat.needsUpdate = true;
    }
  });
}

function solidBox(root: THREE.Object3D): THREE.Box3 {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  let any = false;
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const transparent = mats.every((m) => {
      const mat = m as THREE.MeshStandardMaterial;
      return !!mat && (mat.transparent === true || (mat.opacity ?? 1) < 0.99);
    });
    const name = (mesh.name || "").toLowerCase();
    if (
      transparent ||
      name.includes("_tr") ||
      name.includes("shadow") ||
      name === "__hitbox" ||
      name === "__glow" ||
      mesh.name === "__hitbox" ||
      mesh.name === "__glow"
    ) {
      return;
    }
    mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
    if (!any) {
      box.copy(tmp);
      any = true;
    } else box.union(tmp);
  });
  if (!any) box.setFromObject(root);
  return box;
}

function isSaneCar(root: THREE.Object3D): boolean {
  const box = solidBox(root);
  const size = new THREE.Vector3();
  box.getSize(size);
  const len = Math.max(size.x, size.z);
  const width = Math.min(size.x, size.z);
  return (
    len >= 0.85 &&
    width >= 0.28 &&
    size.y >= 0.18 &&
    size.x * size.y * size.z >= 0.08
  );
}

/** Normalize a car so length runs along Z (track) and it faces -Z like the player. */
function normalizeRivalTemplate(scene: THREE.Object3D): THREE.Object3D | null {
  const root = new THREE.Group();
  const clone = scene.clone(true);
  stripJunkMeshes(clone);
  dropTinyMeshes(clone);
  hardenMaterials(clone);
  root.add(clone);

  let box = solidBox(root);
  let size = new THREE.Vector3();
  box.getSize(size);

  // Standing-on-nose exports: Y is the long axis — lay flat first.
  if (size.y > size.x && size.y > size.z) {
    clone.rotation.x -= Math.PI / 2;
    box = solidBox(root);
    box.getSize(size);
  }

  // Prefer longest axis along Z (road).
  if (size.x > size.z) {
    clone.rotation.y += Math.PI / 2;
    box = solidBox(root);
    box.getSize(size);
  }

  // Face -Z (same direction as player / traffic).
  clone.rotation.y += Math.PI;
  box = solidBox(root);
  box.getSize(size);

  const center = new THREE.Vector3();
  box.getCenter(center);
  const scale = Math.min(
    RIVAL_TARGET_LEN / Math.max(size.x, size.z, 0.001),
    RIVAL_MAX_HEIGHT / Math.max(size.y, 0.001),
  );
  clone.scale.setScalar(scale);
  clone.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);

  box = solidBox(root);
  clone.position.y -= box.min.y;

  if (!isSaneCar(root)) {
    console.warn("[hazards] skipped degenerate rival template");
    return null;
  }

  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
  root.userData.fromGltf = true;
  return root;
}

/** Local XZ half-extents of the visible mesh (skips debug hitboxes). */
export function measureLocalXZ(root: THREE.Object3D): {
  cx: number;
  cz: number;
  halfX: number;
  halfZ: number;
} {
  const box = solidBox(root);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  root.updateMatrixWorld(true);
  root.worldToLocal(center);
  return {
    cx: center.x,
    cz: center.z,
    halfX: Math.max(0.12, size.x / 2),
    halfZ: Math.max(0.12, size.z / 2),
  };
}

/** Shift children so the visual AABB sits on the object's origin. */
export function centerOnAabb(root: THREE.Object3D): {
  halfX: number;
  halfZ: number;
} {
  const m = measureLocalXZ(root);
  if (Math.abs(m.cx) > 0.01 || Math.abs(m.cz) > 0.01) {
    for (const child of root.children) {
    if (child.name === "__hitbox" || child.name === "__glow") continue;
      child.position.x -= m.cx;
      child.position.z -= m.cz;
    }
  }
  const fitted = measureLocalXZ(root);
  return { halfX: fitted.halfX, halfZ: fitted.halfZ };
}

/** Sit wheels on the road after XZ centering. */
export function plantOnGround(root: THREE.Object3D) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (!Number.isFinite(box.min.y)) return;
  const dy = box.min.y;
  if (Math.abs(dy) < 0.002) return;
  for (const child of root.children) {
    if (child.name === "__hitbox" || child.name === "__glow") continue;
    child.position.y -= dy;
  }
}

/** Warm GLB templates once (safe to call from React mount). */
export function preloadHazardModels(): Promise<void> {
  if (ready) return Promise.resolve();
  if (loading) return loading;

  loading = (async () => {
    const loader = new GLTFLoader();
    await Promise.all(
      RIVAL_PATHS.map(
        (path) =>
          new Promise<void>((resolve) => {
            loader.load(
              path,
              (gltf) => {
                try {
                  const tpl = normalizeRivalTemplate(gltf.scene);
                  if (tpl) rivalTemplates.push(tpl);
                } catch (e) {
                  console.warn("[hazards] normalize failed", path, e);
                }
                resolve();
              },
              undefined,
              (err) => {
                console.warn("[hazards] load failed", path, err);
                resolve();
              },
            );
          }),
      ),
    );
    ready = true;
  })();

  return loading;
}

export function makeHazardObject(
  kind: "box" | "barrier" | "rival" | "truck" | "hole",
): THREE.Object3D {
  if (kind === "barrier") {
    const m = makeBarrierMesh();
    m.userData.fromGltf = false;
    return m;
  }
  if (kind === "box") {
    const m = makeCrateMesh();
    m.userData.fromGltf = false;
    return m;
  }
  if (kind === "hole") {
    const m = makeHoleMesh();
    m.userData.fromGltf = false;
    return m;
  }
  if (kind === "truck") {
    const m = makeTruckMesh();
    m.userData.fromGltf = false;
    return m;
  }

  if (rivalTemplates.length) {
    const tpl =
      rivalTemplates[Math.floor(Math.random() * rivalTemplates.length)]!;
    const clone = tpl.clone(true);
    clone.userData.fromGltf = true;
    if (isSaneCar(clone)) {
      clone.add(makeLaneGlow("#F472B6", 0.72));
      return clone;
    }
  }
  const fallback = makeRivalMesh();
  fallback.userData.fromGltf = false;
  return fallback;
}

/** Don't dispose shared GLB template geometry/materials. */
export function disposeHazardObject(obj: THREE.Object3D) {
  for (const name of ["__hitbox", "__glow"] as const) {
    const extra = obj.getObjectByName(name) as THREE.Mesh | undefined;
    if (!extra) continue;
    extra.geometry.dispose();
    const mat = extra.material;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat.dispose();
  }
  if (obj.userData.fromGltf) return;
  obj.traverse((c) => {
    if (c.name === "__hitbox" || c.name === "__glow") return;
    const m = c as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else if (mat) mat.dispose();
  });
}
